import { StVOExaminer } from '../stvo/StVOExaminer';
import { AudioManager } from '../core/AudioManager';

export class ExaminerDialog {
  public container: HTMLElement;
  private avatarEl!: HTMLElement;
  private titleEl!: HTMLElement;
  private messageEl!: HTMLElement;
  private actionEl!: HTMLElement;
  private lastMessageId: string = '';
  private isVisible: boolean = true;
  private autoDismissTimer: any = null;

  // Persistent instruction state — stays until action is completed
  private pendingInstruction: string | null = null;
  private pendingInstructionId: string | null = null;

  constructor(private examiner: StVOExaminer, private audioManager: AudioManager) {
    this.container = document.createElement('div');
    this.container.id = 'examiner-dialog';
    this.container.style.cssText = `
      position: absolute;
      top: clamp(122px, 18vh, 150px);
      left: 50%;
      transform: translateX(-50%);
      width: min(560px, 94%);
      background: rgba(15, 23, 42, 0.95);
      backdrop-filter: blur(14px);
      border: 2px solid #3b82f6;
      border-radius: 14px;
      padding: 10px 14px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
      z-index: 25;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
      transition: border-color 0.2s, box-shadow 0.2s, opacity 0.3s ease, transform 0.3s ease;
      box-sizing: border-box;
    `;

    // Inject mobile-compact responsive CSS for mobile screens
    if (!document.getElementById('examiner-dialog-mobile-css')) {
      const style = document.createElement('style');
      style.id = 'examiner-dialog-mobile-css';
      style.textContent = `
        @media (max-width: 900px), (max-height: 550px) {
          #examiner-dialog {
            top: 106px !important;
            width: min(420px, 92vw) !important;
            padding: 5px 9px !important;
            gap: 7px !important;
            border-radius: 10px !important;
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.6) !important;
          }
          #examiner-dialog .examiner-avatar {
            width: 28px !important;
            height: 28px !important;
            font-size: 15px !important;
            margin-top: 1px !important;
          }
          #examiner-dialog .examiner-msg {
            font-size: 11px !important;
            line-height: 1.25 !important;
          }
          #examiner-dialog .examiner-title {
            font-size: 9.5px !important;
          }
          #examiner-dialog button {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            min-height: 32px !important;
            font-size: 12px !important;
            border-radius: 6px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    this.buildUI();

    // Tap/click anywhere on dialog to dismiss
    this.container.addEventListener('click', (e) => {
      // If clicking voice button, don't dismiss
      if ((e.target as HTMLElement)?.closest('#examiner-voice-replay')) return;
      this.dismiss();
    });

    // If examiner talk-back is OFF (default), start hidden so only the Directions Banner and Mistakes Display show
    if (this.examiner.talkBackEnabled === false) {
      this.isVisible = false;
      this.container.style.display = 'none';
      this.container.style.opacity = '0';
      this.container.style.pointerEvents = 'none';
    } else {
      // Initial auto-dismiss (4.5s on mobile, 8s on desktop)
      const isMobileScreen = typeof window !== 'undefined' && (window.innerWidth < 900 || window.innerHeight < 550);
      this.resetAutoDismiss(isMobileScreen ? 4500 : 8000);
    }
  }

  private buildUI() {
    // Avatar
    this.avatarEl = document.createElement('div');
    this.avatarEl.className = 'examiner-avatar';
    this.avatarEl.style.cssText = `
      width: 40px; height: 40px; border-radius: 50%;
      background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
      border: 2px solid #60a5fa;
      display: flex; justify-content: center; align-items: center;
      font-size: 20px; flex-shrink: 0; margin-top: 2px;
    `;
    this.avatarEl.innerHTML = '👨‍✈️';

    const contentBox = document.createElement('div');
    contentBox.style.cssText = `display: flex; flex-direction: column; gap: 3px; flex-grow: 1; min-width: 0;`;

    // Title row
    const titleRow = document.createElement('div');
    titleRow.style.cssText = `display: flex; justify-content: space-between; align-items: center;`;

    this.titleEl = document.createElement('span');
    this.titleEl.className = 'examiner-title';
    this.titleEl.style.cssText = `font-size: 11px; font-weight: 700; color: #60a5fa; text-transform: uppercase; letter-spacing: 0.5px;`;
    this.titleEl.innerText = this.examiner.name;

    const btnGroup = document.createElement('div');
    btnGroup.style.cssText = `display: flex; align-items: center; gap: 8px;`;

    // Voice replay button
    const replayBtn = document.createElement('button');
    replayBtn.id = 'examiner-voice-replay';
    replayBtn.innerHTML = '🔊';
    replayBtn.title = 'Anweisung wiederholen';
    replayBtn.style.cssText = `
      background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2);
      border-radius: 8px; cursor: pointer;
      font-size: 16px; width: 44px; height: 44px; min-width: 44px; min-height: 44px;
      color: #e2e8f0; display: flex; align-items: center; justify-content: center;
      touch-action: manipulation; box-sizing: border-box;
    `;
    replayBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      (e.currentTarget as HTMLElement)?.blur();
      if (this.messageEl.innerText) {
        this.audioManager.speakExaminer(this.messageEl.innerText);
      }
    });

    // Close / Dismiss Button (✕)
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '✕';
    closeBtn.title = 'Meldung schließen';
    closeBtn.style.cssText = `
      background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2);
      border-radius: 8px; cursor: pointer;
      font-size: 15px; font-weight: bold; width: 44px; height: 44px; min-width: 44px; min-height: 44px;
      color: #94a3b8; display: flex; align-items: center; justify-content: center;
      touch-action: manipulation; box-sizing: border-box;
    `;
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      (e.currentTarget as HTMLElement)?.blur();
      this.dismiss();
    });

    btnGroup.appendChild(replayBtn);
    btnGroup.appendChild(closeBtn);

    titleRow.appendChild(this.titleEl);
    titleRow.appendChild(btnGroup);

    // Main message (instruction)
    this.messageEl = document.createElement('p');
    this.messageEl.className = 'examiner-msg';
    this.messageEl.style.cssText = `font-size: 13px; font-weight: 600; color: #f8fafc; line-height: 1.35; margin: 0;`;
    this.messageEl.innerText = 'Willkommen zur Fahrprüfung! Bitte schnallen Sie sich an und wählen Sie den Gang.';

    // Pending action indicator (persists until action complete)
    this.actionEl = document.createElement('div');
    this.actionEl.style.cssText = `
      font-size: 11px; font-weight: 600; color: #fbbf24;
      background: rgba(251,191,36,0.1); border: 1px solid rgba(251,191,36,0.3);
      border-radius: 6px; padding: 2px 6px; margin-top: 2px;
      display: none;
    `;

    // Tap to dismiss hint
    const dismissHint = document.createElement('div');
    dismissHint.style.cssText = `font-size: 10px; color: #64748b; margin-top: 1px;`;
    dismissHint.innerText = 'Tippen oder [✕] zum Schließen';

    contentBox.appendChild(titleRow);
    contentBox.appendChild(this.messageEl);
    contentBox.appendChild(this.actionEl);
    contentBox.appendChild(dismissHint);

    this.container.appendChild(this.avatarEl);
    this.container.appendChild(contentBox);
  }

  public dismiss() {
    if (!this.isVisible) return;
    this.isVisible = false;
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
    this.container.style.opacity = '0';
    this.container.style.transform = 'translate(-50%, -20px) scale(0.95)';
    this.container.style.pointerEvents = 'none';
    setTimeout(() => {
      if (!this.isVisible) {
        this.container.style.display = 'none';
      }
    }, 300);
  }

  public setTalkBackEnabled(enabled: boolean) {
    this.examiner.setTalkBackEnabled?.(enabled);
    if (!enabled) {
      this.isVisible = false;
      if (this.autoDismissTimer) {
        clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = null;
      }
      this.container.style.opacity = '0';
      this.container.style.pointerEvents = 'none';
      this.container.style.display = 'none';
    } else if (this.examiner.currentMessage) {
      this.messageEl.innerText = this.examiner.currentMessage.text;
      this.show();
    }
  }

  public show() {
    if (this.examiner.talkBackEnabled === false) {
      this.isVisible = false;
      this.container.style.display = 'none';
      this.container.style.pointerEvents = 'none';
      return;
    }
    this.isVisible = true;
    this.container.style.display = 'flex';
    this.container.style.pointerEvents = 'auto';
    setTimeout(() => {
      this.container.style.opacity = '1';
      this.container.style.transform = 'translateX(-50%) scale(1.0)';
    }, 20);
    this.resetAutoDismiss(8000);
  }

  private resetAutoDismiss(durationMs: number = 8000) {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
    }
    this.autoDismissTimer = setTimeout(() => {
      this.dismiss();
    }, durationMs);
  }

  /**
   * Show a persistent instruction that stays until completeInstruction() is called.
   * Speaks the text immediately if talk-back is enabled.
   */
  public showInstruction(text: string, actionLabel: string) {
    const id = Math.random().toString(36).substring(2, 8);
    this.pendingInstruction = text;
    this.pendingInstructionId = id;

    this.messageEl.innerText = text;
    this.actionEl.innerText = `⏳ ${actionLabel}`;
    this.actionEl.style.display = 'block';

    this.container.style.borderColor = '#f59e0b';
    this.container.style.boxShadow = '0 10px 30px rgba(245,158,11,0.25)';
    this.avatarEl.style.background = 'linear-gradient(135deg, #78350f 0%, #d97706 100%)';
    this.avatarEl.style.borderColor = '#fbbf24';
    this.avatarEl.innerHTML = '👨‍✈️';

    if (this.examiner.talkBackEnabled !== false) {
      this.audioManager.speakExaminer(text);
      this.show();
    }

    return id;
  }

  /** Call when the pending instruction is fulfilled */
  public completeInstruction() {
    this.pendingInstruction = null;
    this.pendingInstructionId = null;
    this.actionEl.style.display = 'none';
    this.container.style.borderColor = '#22c55e';
    this.container.style.boxShadow = '0 10px 30px rgba(34,197,94,0.2)';
    this.avatarEl.style.background = 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)';
    this.avatarEl.style.borderColor = '#4ade80';
    this.avatarEl.innerHTML = '✅';

    if (this.examiner.talkBackEnabled !== false) {
      this.show();
      this.resetAutoDismiss(3000);
    }

    // Reset to blue after 2.5 seconds
    setTimeout(() => {
      this.container.style.borderColor = '#3b82f6';
      this.container.style.boxShadow = '0 10px 30px rgba(0,0,0,0.6)';
      this.avatarEl.style.background = 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)';
      this.avatarEl.style.borderColor = '#60a5fa';
      this.avatarEl.innerHTML = '👨‍✈️';
    }, 2500);
  }

  public update() {
    const msg = this.examiner.currentMessage;
    if (!msg || msg.id === this.lastMessageId) return;

    this.lastMessageId = msg.id;

    // If there's a pending instruction snackbar still showing, only update text if it's a warning
    if (this.pendingInstruction && !msg.isWarning) return;

    this.messageEl.innerText = msg.text;
    this.actionEl.style.display = 'none'; // Warnings don't have pending actions

    if (msg.isWarning) {
      this.container.style.borderColor = '#ef4444';
      this.container.style.boxShadow = '0 10px 30px rgba(239,68,68,0.3)';
      this.avatarEl.style.background = 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)';
      this.avatarEl.style.borderColor = '#f87171';
      this.avatarEl.innerHTML = '🚨';
    } else {
      this.container.style.borderColor = '#3b82f6';
      this.container.style.boxShadow = '0 10px 30px rgba(0,0,0,0.6)';
      this.avatarEl.style.background = 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)';
      this.avatarEl.style.borderColor = '#60a5fa';
      this.avatarEl.innerHTML = '👨‍✈️';
    }

    if (this.examiner.talkBackEnabled !== false) {
      this.show();
    } else {
      this.isVisible = false;
      this.container.style.display = 'none';
      this.container.style.pointerEvents = 'none';
    }
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
  }
}
